import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BookOpen } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { apiGet, apiPost, apiPatch, apiDelete, apiUpload } from '../../../utils/api';
import toast from 'react-hot-toast';
import { modelServiceFetch, modelServicePost, modelServiceUpload } from '../../../utils/modelService';
import DocumentHome from './training/DocumentHome';
import DocumentSetup from './training/DocumentSetup';
import DocumentWorkspace from './training/DocumentWorkspace';
import SaveTemplateModal from './training/SaveTemplateModal';

const STYLES = [
  { id: 'formal', name: 'Formal', description: 'Professional corporate tone' },
  { id: 'conversational', name: 'Conversational', description: 'Approachable and engaging' },
  { id: 'technical', name: 'Technical', description: 'Precise with detailed references' },
];

function TrainingStudio() {
  const { theme } = useTheme();
  useAuth();
  const isDark = theme === 'dark';

  // ==================== View State ====================
  // 'home' | 'setup' | 'workspace'
  const [view, setView] = useState('home');

  // ==================== Document State ====================
  const [documentId, setDocumentId] = useState(null);
  const [topic, setTopic] = useState('');
  const [style, setStyle] = useState('formal');

  // Template state
  const [templateFile, setTemplateFile] = useState(null);
  const [templateFileBytes, setTemplateFileBytes] = useState(null);
  const [templateStructure, setTemplateStructure] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isParsing, setIsParsing] = useState(false);

  // Section instructions (section_id -> instruction text)
  const [sectionInstructions, setSectionInstructions] = useState({});
  const [documentSettings, setDocumentSettings] = useState({
    page_size: 'letter',
    page_numbers: true,
    header_text: '',
    footer_text: '',
    logo_base64: null,
    logo_name: null,
  });

  // Generation state
  const [taskId, setTaskId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generatedSections, setGeneratedSections] = useState([]);
  const [generatedTables, setGeneratedTables] = useState([]);
  const [generatedImages, setGeneratedImages] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const pollRef = useRef(null);

  // Section version history for undo (section_id -> previous content)
  const [sectionHistory, setSectionHistory] = useState({});

  // Persistence
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);

  // Lists
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Chat panel
  const [chatPanelSection, setChatPanelSection] = useState(null);
  const [chatHistories, setChatHistories] = useState({}); // section_id -> [{instruction, response}]

  // Save template modal
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  // ==================== Effects ====================

  // Load documents on mount
  useEffect(() => {
    fetchDocuments();
    fetchTemplates();
  }, []);

  // Cleanup polling
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Autosave every 30 seconds when in workspace with unsaved changes
  useEffect(() => {
    if (view !== 'workspace' || !hasUnsavedChanges || !topic.trim()) return;
    const timer = setTimeout(() => {
      saveDocument('draft');
    }, 30000);
    return () => clearTimeout(timer);
  }, [view, hasUnsavedChanges, generatedSections, generatedImages]);

  // Mark unsaved when content changes
  useEffect(() => {
    if (view === 'workspace' && generatedSections.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [generatedSections, generatedImages]);

  // ==================== API Calls ====================

  const fetchDocuments = async () => {
    setLoadingDocs(true);
    try {
      const res = await apiGet('/training_documents');
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await apiGet('/training_templates');
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const parseTemplate = async () => {
    if (!templateFile && !selectedTemplate) {
      toast.error('Please upload a template or select one from the library');
      return;
    }
    if (!topic.trim()) {
      toast.error('Please enter a training topic');
      return;
    }

    setIsParsing(true);
    try {
      if (selectedTemplate?.template_structure) {
        setTemplateStructure(selectedTemplate.template_structure);
        if (selectedTemplate.template_structure.detected_settings) {
          setDocumentSettings(prev => ({ ...prev, ...selectedTemplate.template_structure.detected_settings }));
        }
        setIsParsing(false);
        return;
      }

      let fileToSend = templateFile;
      if (!fileToSend && selectedTemplate?.template_file_url) {
        const fileRes = await fetch(selectedTemplate.template_file_url);
        const blob = await fileRes.blob();
        fileToSend = new File([blob], `${selectedTemplate.name}.docx`, {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });
        const reader = new FileReader();
        reader.onload = () => setTemplateFileBytes(reader.result.split(',')[1]);
        reader.readAsDataURL(blob);
      }

      if (!fileToSend) {
        toast.error('No template file available');
        return;
      }

      const formData = new FormData();
      formData.append('file', fileToSend);
      const res = await modelServiceUpload('/training/parse-template', formData);
      if (!res.ok) throw new Error('Failed to parse template');
      const structure = await res.json();
      setTemplateStructure(structure);
      // Pre-populate document settings from template's existing header/footer/page size
      if (structure.detected_settings) {
        setDocumentSettings(prev => ({
          ...prev,
          ...structure.detected_settings,
        }));
      }
    } catch (e) {
      toast.error(e.message || 'Failed to parse template');
    } finally {
      setIsParsing(false);
    }
  };

  const startGeneration = async () => {
    setIsGenerating(true);
    try {
      const filteredStructure = {
        ...templateStructure,
        sections: templateStructure.sections.filter(s => s.included !== false),
      };

      const res = await modelServicePost('/training/generate', {
        template_structure: filteredStructure,
        topic,
        style,
        section_instructions: sectionInstructions,
        use_rag: true,
      });

      if (!res.ok) throw new Error('Failed to start generation');
      const data = await res.json();
      setTaskId(data.task_id);
      setView('workspace');
      startPolling(data.task_id);
    } catch (e) {
      toast.error(e.message || 'Failed to start generation');
      setIsGenerating(false);
    }
  };

  const startPolling = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await modelServiceFetch(`/training/generate/status/${id}`);
        const data = await res.json();
        setGenerationStatus(data);

        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGeneratedSections(data.sections || []);
          setGeneratedTables(data.tables || []);
          setGeneratedImages(data.images || {});
          setIsGenerating(false);
          toast.success('Content generation complete!');
          saveDocument('draft');
        } else if (data.status === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setIsGenerating(false);
          toast.error(data.error || 'Generation failed');
        } else if (data.completed_sections) {
          setGeneratedSections(data.completed_sections);
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, 3000);
  };

  const regenerateSection = async (sectionId, customInstruction) => {
    // Stash current version for undo
    const current = generatedSections.find(s => s.id === sectionId);
    if (current?.generated_content) {
      setSectionHistory(prev => ({
        ...prev,
        [sectionId]: current.generated_content,
      }));
    }

    try {
      const res = await modelServicePost('/training/regenerate-section', {
        task_id: taskId,
        section_id: sectionId,
        topic,
        style,
        custom_instruction: customInstruction,
        context_sections: generatedSections.filter(s => s.id !== sectionId),
      });

      if (!res.ok) throw new Error('Failed to regenerate');
      const data = await res.json();

      setGeneratedSections(prev =>
        prev.map(s => s.id === sectionId ? { ...s, generated_content: data.generated_content } : s)
      );
      setHasUnsavedChanges(true);
      toast.success('Section regenerated');
    } catch (e) {
      // Remove stashed version on failure
      setSectionHistory(prev => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      toast.error(e.message);
    }
  };

  const undoRegenerate = (sectionId) => {
    const previous = sectionHistory[sectionId];
    if (!previous) return;

    setGeneratedSections(prev =>
      prev.map(s => s.id === sectionId ? { ...s, generated_content: previous } : s)
    );
    setSectionHistory(prev => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setHasUnsavedChanges(true);
    toast.success('Reverted to previous version');
  };

  const regenerateImage = async (sectionId) => {
    const section = generatedSections.find(s => s.id === sectionId);
    if (!section) return;

    try {
      const res = await modelServicePost('/training/generate-image', {
        section_title: section.title,
        section_content: section.generated_content,
        topic,
        style,
      });

      if (!res.ok) throw new Error('Failed to generate image');
      const data = await res.json();
      setGeneratedImages(prev => ({ ...prev, [sectionId]: data.image_base64 }));
      setHasUnsavedChanges(true);
      toast.success('Image generated');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const chatWithSection = async (sectionId, instruction, fileText = null) => {
    const section = generatedSections.find(s => s.id === sectionId);
    if (!section) return null;

    // Stash current version for undo
    setSectionHistory(prev => ({
      ...prev,
      [sectionId]: section.generated_content,
    }));

    try {
      const history = chatHistories[sectionId] || [];
      const res = await modelServicePost('/training/section-chat', {
        section_title: section.title,
        section_content: section.generated_content,
        instruction,
        topic,
        style,
        file_text: fileText,
        chat_history: history,
      });

      if (!res.ok) throw new Error('Failed to revise section');
      const data = await res.json();

      setGeneratedSections(prev =>
        prev.map(s => s.id === sectionId ? { ...s, generated_content: data.revised_content } : s)
      );

      // Save chat history
      setChatHistories(prev => ({
        ...prev,
        [sectionId]: [
          ...(prev[sectionId] || []),
          { instruction, summary: 'Applied changes' },
        ],
      }));

      setHasUnsavedChanges(true);
      toast.success('Section updated');
      return data.revised_content;
    } catch (e) {
      toast.error(e.message);
      return null;
    }
  };

  const updateSectionContent = useCallback((sectionId, content) => {
    setGeneratedSections(prev =>
      prev.map(s => s.id === sectionId ? { ...s, generated_content: content } : s)
    );
    setHasUnsavedChanges(true);
  }, []);

  const buildAndDownload = async () => {
    // Auto-save before building
    await saveDocument('completed');

    setIsBuilding(true);
    try {
      let templateB64 = templateFileBytes || null;
      if (!templateB64 && selectedTemplate?.template_file_url) {
        const res = await fetch(selectedTemplate.template_file_url);
        const blob = await res.blob();
        templateB64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });
      }
      // templateB64 can be null for scratch-built documents — backend handles it

      const res = await modelServicePost('/training/build-docx', {
        template_base64: templateB64,
        sections: generatedSections,
        generated_images: generatedImages,
        table_data: generatedTables,
        topic,
        document_settings: documentSettings,
      });

      if (!res.ok) throw new Error('Failed to build document');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${topic.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Document downloaded!');
    } catch (e) {
      toast.error(e.message || 'Failed to build document');
    } finally {
      setIsBuilding(false);
    }
  };

  const saveDocument = async (status = 'draft') => {
    if (!topic.trim()) return;
    setIsSaving(true);
    try {
      const payload = {
        title: topic,
        topic,
        status,
        training_template_id: selectedTemplate?.id || null,
        template_structure: templateStructure,
        generated_sections: generatedSections,
        metadata: {
          style,
          generated_at: new Date().toISOString(),
          images: generatedImages,
          tables: generatedTables,
          template_file_bytes: templateFileBytes,
          chat_histories: chatHistories,
          section_instructions: sectionInstructions,
          document_settings: documentSettings,
        },
      };

      let res;
      if (documentId) {
        res = await apiPatch(`/training_documents/${documentId}`, payload);
      } else {
        res = await apiPost('/training_documents', payload);
      }

      if (res.ok) {
        const data = await res.json();
        if (data.document?.id && !documentId) setDocumentId(data.document.id);
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      }
    } catch (e) {
      console.error('Failed to save document:', e);
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== Navigation ====================

  const resetDocument = () => {
    setDocumentId(null);
    setTopic('');
    setStyle('formal');
    setTemplateFile(null);
    setTemplateFileBytes(null);
    setTemplateStructure(null);
    setSelectedTemplate(null);
    setSectionInstructions({});
    setDocumentSettings({ page_size: 'letter', page_numbers: true, header_text: '', footer_text: '', logo_base64: null, logo_name: null });
    setTaskId(null);
    setGenerationStatus(null);
    setGeneratedSections([]);
    setGeneratedTables([]);
    setGeneratedImages({});
    setIsGenerating(false);
    setLastSaved(null);
    setHasUnsavedChanges(false);
    setSectionHistory({});
    setChatPanelSection(null);
    setChatHistories({});
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const goHome = () => {
    if (hasUnsavedChanges && !confirm('You have unsaved changes. Leave without saving?')) return;
    resetDocument();
    setView('home');
    fetchDocuments();
  };

  const startNewDocument = () => {
    resetDocument();
    setView('setup');
  };

  const duplicateDocument = (doc) => {
    resetDocument();
    setTopic('');
    setStyle(doc.metadata?.style || 'formal');
    setTemplateStructure(doc.template_structure);
    setTemplateFileBytes(doc.metadata?.template_file_bytes || null);
    setSelectedTemplate(doc.training_template_id ? { id: doc.training_template_id } : null);
    setSectionInstructions(doc.metadata?.section_instructions || {});
    setView('setup');
  };

  const loadDocument = async (doc) => {
    // If the list endpoint didn't include full data, fetch the individual document
    let fullDoc = doc;
    if (!doc.generated_sections && doc.id) {
      try {
        const res = await apiGet(`/training_documents/${doc.id}`);
        if (res.ok) {
          const data = await res.json();
          fullDoc = data.document || doc;
        }
      } catch (e) {
        console.error('Failed to fetch full document:', e);
      }
    }

    resetDocument();
    setDocumentId(fullDoc.id);
    setTopic(fullDoc.topic || fullDoc.title || '');
    setStyle(fullDoc.metadata?.style || 'formal');
    setTemplateStructure(fullDoc.template_structure);
    setGeneratedSections(fullDoc.generated_sections || []);
    setGeneratedTables(fullDoc.metadata?.tables || []);
    setGeneratedImages(fullDoc.metadata?.images || {});
    setTemplateFileBytes(fullDoc.metadata?.template_file_bytes || null);
    setSelectedTemplate(fullDoc.training_template_id ? { id: fullDoc.training_template_id } : null);
    setChatHistories(fullDoc.metadata?.chat_histories || {});
    setSectionInstructions(fullDoc.metadata?.section_instructions || {});
    if (fullDoc.metadata?.document_settings) setDocumentSettings(fullDoc.metadata.document_settings);
    setLastSaved(new Date(fullDoc.updated_at));

    const hasSections = Array.isArray(fullDoc.generated_sections) && fullDoc.generated_sections.length > 0;
    if (hasSections) {
      setView('workspace');
    } else if (fullDoc.template_structure) {
      setView('setup');
    } else {
      setView('setup');
    }
  };

  const deleteDocument = async (id) => {
    try {
      await apiDelete(`/training_documents/${id}`);
      setDocuments(prev => prev.filter(d => d.id !== id));
      toast.success('Document deleted');
    } catch (e) {
      toast.error('Failed to delete document');
    }
  };

  const handleFileUpload = (file) => {
    setTemplateFile(file);
    setSelectedTemplate(null);
    const reader = new FileReader();
    reader.onload = () => setTemplateFileBytes(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateFile(null);
    setTemplateFileBytes(null);
    if (template.template_structure) {
      setTemplateStructure(template.template_structure);
    }
  };

  const clearTemplate = () => {
    setSelectedTemplate(null);
    setTemplateFile(null);
    setTemplateFileBytes(null);
    setTemplateStructure(null);
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      const formData = new FormData();
      formData.append('name', templateData.name);
      formData.append('description', templateData.description || '');
      formData.append('category', templateData.category || '');
      formData.append('is_shared', templateData.is_shared || false);
      if (templateStructure) {
        formData.append('template_structure', JSON.stringify(templateStructure));
      }
      if (templateFile) {
        formData.append('template_file', templateFile);
      }

      const res = await apiUpload('/training_templates', formData);
      if (!res.ok) throw new Error('Failed to save template');
      toast.success('Template saved to library!');
      setShowSaveTemplate(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const createFromScratch = (sectionTitles) => {
    const sections = sectionTitles.map((title, i) => ({
      id: crypto.randomUUID(),
      index: i,
      type: 'section',
      level: 1,
      title,
      paragraph_index: -1,
      style: 'Heading 1',
      heading_style: {},
      content_blocks: [],
      is_placeholder: true,
      has_image_slot: false,
      included: true,
      is_new: true,
    }));

    setTemplateStructure({
      sections,
      tables: [],
      image_positions: [],
      document_sections: [],
      template_markdown: '',
      stats: {
        section_count: sections.length,
        table_count: 0,
        image_count: 0,
        placeholder_count: 0,
        total_paragraphs: 0,
      },
    });
    // No template file needed — build_output_docx will create all sections as "new"
    setTemplateFile(null);
    setTemplateFileBytes(null);
    setSelectedTemplate(null);
  };

  const handleToggleSection = (id) => {
    setTemplateStructure(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === id ? { ...s, included: !s.included } : s
      ),
    }));
  };

  const handleAddSection = (afterSectionId, title) => {
    const newSection = {
      id: crypto.randomUUID(),
      index: templateStructure.sections.length,
      type: 'section',
      level: 1,
      title,
      paragraph_index: -1,
      style: 'Heading 1',
      heading_style: {},
      content_blocks: [],
      is_placeholder: true,
      has_image_slot: false,
      included: true,
      is_new: true,
    };

    setTemplateStructure(prev => {
      const sections = [...prev.sections];
      if (afterSectionId === 'end') {
        sections.push(newSection);
      } else {
        const idx = sections.findIndex(s => s.id === afterSectionId);
        sections.splice(idx + 1, 0, newSection);
      }
      return { ...prev, sections, stats: { ...prev.stats, section_count: sections.length } };
    });
  };

  const deleteTemplate = async (id) => {
    try {
      await apiDelete(`/training_templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error('Failed to delete template');
    }
  };

  // ==================== Render ====================

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b shrink-0 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {view !== 'home' && (
              <button
                onClick={goHome}
                className={`p-1.5 rounded-lg mr-1 transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                title="Back to documents"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </button>
            )}
            <BookOpen size={24} className="text-blue-500" />
            <div>
              <h1 className="text-xl font-bold">Training Studio</h1>
              {view === 'home' && (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Generate training documents from templates with AI
                </p>
              )}
              {view === 'setup' && (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Configure your new document
                </p>
              )}
              {view === 'workspace' && topic && (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {topic}
                  {lastSaved && (
                    <span className={`ml-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {isSaving ? 'Saving...' : `Saved ${lastSaved.toLocaleTimeString()}`}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {view === 'home' && (
          <DocumentHome
            isDark={isDark}
            documents={documents}
            loadingDocs={loadingDocs}
            onNewDocument={startNewDocument}
            onLoadDocument={loadDocument}
            onDuplicateDocument={duplicateDocument}
            onDeleteDocument={deleteDocument}
          />
        )}

        {view === 'setup' && (
          <DocumentSetup
            isDark={isDark}
            topic={topic}
            style={style}
            styles={STYLES}
            templateFile={templateFile}
            selectedTemplate={selectedTemplate}
            templateStructure={templateStructure}
            templates={templates}
            loadingTemplates={loadingTemplates}
            isParsing={isParsing}
            isGenerating={isGenerating}
            sectionInstructions={sectionInstructions}
            documentSettings={documentSettings}
            onDocumentSettingsChange={setDocumentSettings}
            onTopicChange={setTopic}
            onStyleChange={setStyle}
            onFileUpload={handleFileUpload}
            onSelectTemplate={handleSelectTemplate}
            onParse={parseTemplate}
            onToggleSection={handleToggleSection}
            onAddSection={handleAddSection}
            onSectionInstructionChange={(id, val) => setSectionInstructions(prev => ({ ...prev, [id]: val }))}
            onGenerate={startGeneration}
            onSaveTemplate={() => setShowSaveTemplate(true)}
            onDeleteTemplate={deleteTemplate}
            onCreateFromScratch={createFromScratch}
            onClearTemplate={clearTemplate}
            onBack={goHome}
          />
        )}

        {view === 'workspace' && (
          <DocumentWorkspace
            isDark={isDark}
            topic={topic}
            sections={generatedSections}
            images={generatedImages}
            isGenerating={isGenerating}
            generationStatus={generationStatus}
            sectionHistory={sectionHistory}
            chatPanelSection={chatPanelSection}
            chatHistories={chatHistories}
            isBuilding={isBuilding}
            onUpdateSection={updateSectionContent}
            onRegenerateSection={regenerateSection}
            onUndoRegenerate={undoRegenerate}
            onRegenerateImage={regenerateImage}
            onChatWithSection={chatWithSection}
            onOpenChat={setChatPanelSection}
            onCloseChat={() => setChatPanelSection(null)}
            onBuild={buildAndDownload}
            onBackToSetup={() => {
              if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
              }
              setView('setup');
            }}
          />
        )}
      </div>

      {/* Save Template Modal */}
      {showSaveTemplate && (
        <SaveTemplateModal
          isDark={isDark}
          onSave={handleSaveTemplate}
          onClose={() => setShowSaveTemplate(false)}
        />
      )}
    </div>
  );
}

export default TrainingStudio;
